/**
 * Hộp "Chọn bảng từ CSDL" — phần thuần của `KhaiBangDialog.tsx` native.
 *
 * Đo 08/09/2026 trên một CSDL SAP Business One: 2.832 đối tượng, 79% hoàn toàn rỗng (SAP dựng sẵn bảng cho mọi phân hệ
 * dù đơn vị không dùng). Mọi luật ở đây — mặc định chọn theo quy mô, xếp bảng có dữ liệu lên trước, gom cảnh báo, bỏ cột
 * rỗng — sinh ra từ con số ấy.
 */
import type { BangBoQua, IntrospectBang, LyDoBoQua } from '../types.ts';
import { cotKhoa } from './chon-cot.ts';

/** Trần NHẮC (không chặn): mỗi bảng là một bảng trong kho bản sao, chép lại mỗi chu kỳ, và một dòng trong lược đồ gửi mô hình. */
export const NGUONG_NHAC = 200;

/** Dựng bao nhiêu hàng một lúc: 2.832 nút cùng lúc làm hộp thoại đứng hình vài giây — và người dùng tìm, không đọc hết. */
export const MOI_LAN = 150;

/** Bảng ≥ ngưỡng cột là nhóm làm gói ngữ cảnh phình (~50 token mỗi cột ở MỌI lượt hỏi). */
export const NGUONG_NHIEU_COT = 80;

export type LocNhanh = 'co_dulieu' | 'da_khai' | 'rong_cot';

export const THU_TU_LY_DO: LyDoBoQua[] = ['qua_nhieu_cot', 'khoa_chinh_qua_dai', 'khoa_chinh_nullable', 'khac'];

/** Bảng KHÁCH tự tạo theo quy ước SAP B1: tiền tố `@` ở TÊN GỐC (`sourceType`), không ở tên đã chuẩn hoá. */
export const cuaKhach = (b: IntrospectBang) => b.sourceType.startsWith('@');

/** Đã ĐO ĐƯỢC là có dữ liệu. Chưa biết (`undefined`/`null`) xếp cùng nhóm có dữ liệu: chưa biết khác biết là rỗng. */
export const coDuLieu = (b: IntrospectBang) => b.soDong === undefined || b.soDong === null || b.soDong > 0;

/**
 * Lựa chọn ban đầu khi mở hộp: đã khai gì thì tích đúng cái đó (hộp này mở ra để LẤY THAY ĐỔI của lược đồ); chưa khai gì
 * thì dưới ngưỡng chọn hết, trên ngưỡng để trống và mời tìm — 0 lựa chọn nói đúng sự thật: chưa ai quyết định gì.
 */
export function chonBanDau(ten: string[], daKhai: string[]): Set<string> {
  const cu = new Set(daKhai);
  const daCo = ten.filter((x) => cu.has(x));
  return daCo.length ? new Set(daCo) : new Set(ten.length > NGUONG_NHAC ? [] : ten);
}

/**
 * Cột BỎ SẴN theo bản khai: với bảng đã khai, cột không có trong bản khai thì bỏ sẵn — người vận hành từng bỏ nó. Cột
 * KHOÁ thì không bao giờ. ⚠️ Cột nguồn VỪA THÊM cũng rơi vào đây (nhìn từ đây hai ca giống hệt) — nên đếm `cotMoi` để nói ra.
 */
export function boCotBanDau(
  bang: IntrospectBang[],
  daKhai: { name: string; columns?: { name: string }[] }[],
): Record<string, Set<string>> {
  const theoTen = new Map(daKhai.map((b) => [b.name, b]));
  const bo: Record<string, Set<string>> = {};
  for (const b of bang) {
    const c = theoTen.get(b.suggestion.name);
    if (!c) continue;
    const daKhaiCot = new Set((c.columns ?? []).map((x) => x.name));
    const khoa = cotKhoa(b.suggestion);
    const thua = (b.suggestion.columns ?? []).map((x) => x.name).filter((x) => !daKhaiCot.has(x) && !khoa.has(x));
    if (thua.length) bo[b.suggestion.name] = new Set(thua);
  }
  return bo;
}

/** Số cột có ở nguồn mà bản khai hiện tại chưa có, theo bảng. */
export function demCotMoi(
  bang: IntrospectBang[],
  daKhai: { name: string; columns?: { name: string }[] }[],
): Map<string, number> {
  const m = new Map<string, number>();
  for (const b of daKhai) {
    const dx = bang.find((x) => x.suggestion.name === b.name);
    if (!dx) continue;
    const cu = new Set((b.columns ?? []).map((x) => x.name));
    const n = (dx.suggestion.columns ?? []).filter((c) => !cu.has(c.name)).length;
    if (n) m.set(b.name, n);
  }
  return m;
}

/**
 * Cột ĐÃ ĐO và không có một giá trị nào, theo bảng. `nonNull === 0` (không phải `distinct`, có thể bỏ trống ở cột quá
 * nhiều giá trị). Đòi `rowCount > 0`: bảng chưa có dòng nào thì MỌI cột đều rỗng, bỏ hết là bản khai không cột — 422.
 */
export function cotRongTheoBang(hoSoTables: Record<string, unknown>[]): Map<string, Set<string>> {
  const m = new Map<string, Set<string>>();
  for (const t of hoSoTables as { table?: string; rowCount?: number; columns?: { column?: string; nonNull?: number }[] }[]) {
    if (!t.table || !t.rowCount) continue;
    const rong = (t.columns ?? []).filter((c) => c.column && (c.nonNull ?? 0) === 0).map((c) => c.column as string);
    if (rong.length) m.set(t.table, new Set(rong));
  }
  return m;
}

/** Cột rỗng CÒN ĐANG GIỮ — đã trừ cột khoá (ràng buộc của `ReplicaTable`) và cột đã bỏ. */
export function cotRongConGiu(
  bang: IntrospectBang[],
  cotRong: Map<string, Set<string>>,
  boCot: Record<string, Set<string>>,
): { bang: string; cot: string[] }[] {
  const ra: { bang: string; cot: string[] }[] = [];
  for (const b of bang) {
    const ten = b.suggestion.name;
    const rong = cotRong.get(ten);
    if (!rong) continue;
    const khoa = cotKhoa(b.suggestion);
    const daBo = boCot[ten] ?? new Set<string>();
    const cot = (b.suggestion.columns ?? []).map((x) => x.name).filter((x) => rong.has(x) && !khoa.has(x) && !daBo.has(x));
    if (cot.length) ra.push({ bang: ten, cot });
  }
  return ra;
}

/**
 * Lọc theo tên VÀ tiêu đề (người vận hành nhớ "hoá đơn" chứ không nhớ `OINV`), rồi XẾP: bảng `@` của khách → bảng có
 * dữ liệu → theo tên. Xếp theo chữ cái ở một ERP đóng gói là 2.832 dòng giống hệt nhau.
 */
export function locVaXep(
  bang: IntrospectBang[],
  tuyChon: { tim: string; anRong: boolean; locNhanh: LocNhanh | null; chon: Set<string> },
): IntrospectBang[] {
  const q = tuyChon.tim.trim().toLowerCase();
  let ds = bang.filter(
    (b) => !q || b.suggestion.name.toLowerCase().includes(q) || (b.suggestion.title ?? '').toLowerCase().includes(q),
  );
  if (tuyChon.anRong) ds = ds.filter(coDuLieu);
  if (tuyChon.locNhanh === 'co_dulieu') ds = ds.filter(coDuLieu);
  else if (tuyChon.locNhanh === 'da_khai') ds = ds.filter((b) => tuyChon.chon.has(b.suggestion.name));
  else if (tuyChon.locNhanh === 'rong_cot') ds = ds.filter((b) => (b.suggestion.columns?.length ?? 0) >= NGUONG_NHIEU_COT);
  return [...ds].sort(
    (x, y) =>
      Number(cuaKhach(y)) - Number(cuaKhach(x)) ||
      Number(coDuLieu(y)) - Number(coDuLieu(x)) ||
      x.suggestion.name.localeCompare(y.suggestion.name),
  );
}

/**
 * Cảnh báo GOM THEO MẪU, không theo bảng: trình dò sinh cùng một câu cho mọi bảng thiếu cùng một thứ, và thứ lặp ở mọi
 * dòng là nhiễu. Khoá gom bỏ phần trong nháy đơn — "ĐOÁN là 'updatedAt'" và "ĐOÁN là 'ngay_sua'" là cùng một chuyện.
 */
export function gomCanhBao(bang: IntrospectBang[]): { n: number; mau: string }[] {
  const m = new Map<string, { n: number; mau: string }>();
  for (const b of bang) {
    for (const n of b.notes ?? []) {
      const k = n.replace(/'[^']*'/g, '…');
      const cu = m.get(k);
      if (cu) cu.n += 1;
      else m.set(k, { n: 1, mau: k });
    }
  }
  return [...m.values()].sort((x, y) => y.n - x.n);
}

/** Bảng bị loại khỏi bản khai đề xuất, gộp theo lý do theo thứ tự cố định. */
export function nhomBoQua(boQua: BangBoQua[]): (readonly [LyDoBoQua, string[]])[] {
  const m = new Map<LyDoBoQua, string[]>();
  for (const x of boQua) m.set(x.lyDo, [...(m.get(x.lyDo) ?? []), x.bang]);
  return THU_TU_LY_DO.filter((k) => m.has(k)).map((k) => [k, m.get(k) ?? []] as const);
}

/** Đề xuất gửi lên khi Lưu: chỉ thay `columns` của bảng có cột bỏ, giữ nguyên phần còn lại (máy chủ kiểm đủ `ReplicaTable`). */
export function goiLuu(
  bang: IntrospectBang[],
  chon: Set<string>,
  boCot: Record<string, Set<string>>,
): Record<string, unknown>[] {
  return bang
    .filter((b) => chon.has(b.suggestion.name))
    .map((b) => {
      const bo = boCot[b.suggestion.name];
      if (!bo?.size) return b.suggestion;
      return { ...b.suggestion, columns: (b.suggestion.columns ?? []).filter((c) => !bo.has(c.name)) };
    });
}
